import { concat } from 'lodash';
import { useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import {
  useGetAssemblies,
  useGetAssembliesWithoutInclude,
  useParamQuery,
  useUpdateAssemblyWithAssemblyTags,
} from 'src/hooks';
import { useGetAllAssemblyTags } from 'src/hooks/api/assemblyTags/useGetAllAssemblyTags';
import { useUpdateAssemblyTag } from 'src/hooks/api/assemblyTags/useUpdateAssemblyTag';
import { formatQuery } from 'src/utils';

export const useEditAssemblyTagPage = (assemblyTagId: number) => {
  const { onChangeParams } = useParamQuery();
  const { userRole } = useContext(AuthDataProvider);
  const location = useLocation();

  const {
    data: persistedSelectedAssemblies,
    isLoading: isSelectedDataLoadComplete,
    isSuccess: isSelectedDataSuccess,
  } = useGetAssembliesWithoutInclude([
    `assembly_tag_id=${assemblyTagId}`,
    'sort=-created_at',
  ]);

  const {
    data: selectedPageData,
    isLoading: isSelectedPageDataLoadComplete,
    isSuccess: isSelectedPageDataSuccess,
  } = useGetAssembliesWithoutInclude(
    concat(formatQuery(location.search), [
      `assembly_tag_id=${assemblyTagId}`,
      'sort=-created_at',
    ]),
  );

  const {
    data,
    isLoading: isLoadingAssemblies,
    error: errorLoadingAssemblies,
    isSuccess: isAssemblySuccess,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetAssemblies(
    concat(formatQuery(location.search), [
      selectedPageData?.length
        ? `id.notlist=${selectedPageData.map((item) => item.id).join('|')}`
        : '',
      'sort=-created_at',
    ]),
    Boolean(isSelectedPageDataSuccess === true),
  );

  const { data: assemblyTags = [], isLoading: isLoadingAssemblyTags } =
    useGetAllAssemblyTags(userRole);

  const selectedAssemblyTag = assemblyTags.find(
    (assemblyTag) => assemblyTag.id === assemblyTagId,
  );

  const updateAssemblyTag = useUpdateAssemblyTag();
  const updateAssemblies = useUpdateAssemblyWithAssemblyTags();

  const getNextAssemblyPage = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  return {
    data,
    persistedSelectedAssemblies,
    isSelectedDataLoadComplete,
    isSelectedDataSuccess,
    selectedPageData,
    isSelectedPageDataLoadComplete,
    isSelectedPageDataSuccess,
    isAssemblySuccess,
    getNextAssemblyPage,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoadingAssemblyTags,
    errorLoadingAssemblies,
    isLoadingAssemblies,
    onChangeParams,
    updateAssemblyTag,
    updateAssemblies,
    selectedAssemblyTag,
  };
};
